import type { CoachConfig } from "./coach-params";

// Coach segmentation (a leaf): split a piece into the overlapping CHAINS the pool
// rotates among. Pure and deterministic (only interior chain directions consume the
// injected RNG). Extracted from coach.ts to keep it under the size cap.

export type ChainDir = "forward" | "backward";

/** A contiguous chain descriptor produced by {@link segment}. */
export interface Chain {
  startBar: number;
  endBar: number;
  dir: ChainDir;
}

/**
 * Segment a piece into chains. `singleChain` (or a piece below `minChainSplitBars`)
 * yields ONE forward whole-piece passage. Otherwise ≥2 roughly-equal chains, first
 * forward / last backward, interior by `pBackChain`, each extended `overlapBars`
 * past internal seams. `rng` injected for determinism (only interior dirs use it).
 */
export function segment(barCount: number, c: CoachConfig, rng: () => number): Chain[] {
  if (c.singleChain || barCount < c.minChainSplitBars) {
    return [{ startBar: 0, endBar: barCount, dir: "forward" }];
  }
  const segCount = Math.max(2, Math.ceil(barCount / c.chainMaxBars));
  const chains: Chain[] = [];
  for (let i = 0; i < segCount; i++) {
    const rawStart = Math.floor((i * barCount) / segCount);
    const rawEnd = Math.floor(((i + 1) * barCount) / segCount);
    const startBar = i === 0 ? 0 : Math.max(0, rawStart - c.overlapBars);
    const endBar = i === segCount - 1 ? barCount : Math.min(barCount, rawEnd + c.overlapBars);
    const dir: ChainDir =
      i === 0
        ? "forward"
        : i === segCount - 1
          ? "backward"
          : rng() < c.pBackChain
            ? "backward"
            : "forward";
    chains.push({ startBar, endBar, dir });
  }
  return chains;
}
